import type { IAgentToolCall } from "./agentAnswer.types";

/** Client-side display rules for agent output: plurals and failure copy. */

/**
 * Pluralizes an English noun.
 *
 * The service names entities in the singular ("bill", "jobsite"). One place to
 * pluralize, so callers never double up.
 */
export function pluralize(noun: string, count: number): string {
    if (count === 1) {
        return noun;
    }
    if (/(s|x|z|ch|sh)$/i.test(noun)) {
        return `${noun}es`;
    }
    if (/[^aeiou]y$/i.test(noun)) {
        return `${noun.slice(0, -1)}ies`;
    }
    return `${noun}s`;
}

/** Why a turn ended without an answer, as far as the client can tell. */
export type AgentFailure =
    /** The request never got a response: offline, or a proxy error with no CORS headers. */
    | { kind: "offline" }
    /** An HTTP status, from the response or from the stream's `error` event. */
    | { kind: "status"; status: number }
    /** The body ended with no `final`. */
    | { kind: "cut" }
    /** Nothing arrived for longer than the client waits. */
    | { kind: "timeout" };

/**
 * What the user reads when a turn fails. Never the server's text: that is
 * written for logs ("agent engine failed: DeadlineExceeded: …").
 */
export function describeAgentFailure(failure: AgentFailure): string {
    switch (failure.kind) {
        case "offline":
            return "Couldn't reach Ask. Check your connection.";
        case "cut":
            return "The answer stopped partway through.";
        case "timeout":
            return "Ask took too long to answer.";
        case "status":
            if (failure.status === 401 || failure.status === 403) {
                return "Ask couldn't confirm your sign-in. If this keeps happening, refresh the page.";
            }
            if (failure.status === 404) {
                return "Ask lost track of this conversation, so trying again starts a new one.";
            }
            if (failure.status === 429) {
                return "Ask is busy right now.";
            }
            return "Ask ran into a problem answering that.";
    }
}

export const GENERIC_STEP_LABEL = "Working on it";

/**
 * The server's `label`, as sent. Never built from `input`: that is the model's
 * raw text (handles, slugs, queries) and reads as noise.
 */
export function stepLabel(call: IAgentToolCall): string {
    const label = call.label.trim();
    return label ? label : GENERIC_STEP_LABEL;
}
